// #4401: opening the local store under a DIFFERENT NAME, as the way out of a store that
// has stopped answering.
//
// Why a new name rather than deleting the old one. A delete waits for every open
// connection to close, and against a store that answers nothing it can queue for ever
// exactly like the jam it is meant to clear — the app's own sign-out wipe already has to
// cope with a delete that fires no event at all. Opening a name nothing has ever touched
// cannot queue behind anything, so it is the one reset that is guaranteed to finish.
//
// What it costs. The old store is left on disk, holding whatever had not yet been
// synced from this device. Everything the server already has is downloaded again into
// the new one, so a user whose device had finished syncing loses nothing at all — and a
// user whose device had never synced has nothing to lose by definition, which the app
// can check for itself (see `hasSyncedOnThisDeviceMarker`).
//
// The generation number is kept in localStorage: a different storage system from the one
// that jams, and one that keeps working when it does.
//
// Injectable storage, no Angular imports: the unit test drives it with a plain object.

/** The subset of localStorage this needs — injected so a test can supply a plain object. */
export interface GenerationStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

// ---------------------------------------------------------------------------
// #6788: may the app switch to a fresh name BY ITSELF?
//
// The button above costs whatever the device had not synced, which is why it asks. A store
// this version provably CREATED here (`LocalStoreOrigin` below), that never finished a
// download here AND never confirmed a single write, holds nothing the server lacks, so for
// that store the app may switch on its own. The origin is what makes the other two
// trustworthy: missing markers alone are not proof. Two more markers answer the rest, all in
// localStorage for the same reason the generation is:
//
//  - "written": set the first time the store CONFIRMS a write of user data, because only a
//    confirmed write is known to be in it. A save still under way is a separate fact
//    (`userWritesInFlight`) and blocks the switch on its own. Keyed on the name actually
//    opened, so a fresh generation starts unwritten.
//  - "auto-switched": set once the app has switched on its own. Keyed on the base name,
//    so the app does it at most once per account on this device. If the fresh store goes
//    silent too, the ordinary steps (reconnect, watchdog, the error page and its button)
//    take over instead of a reload loop.
//
// See design-docs/boot-sequence.md, "#6788: a brand-new silent store is switched by itself".
// ---------------------------------------------------------------------------

/**
 * Where this device's copy of a store came from, as far as the app can prove (#6788).
 *
 * "Never synced here" and "no written marker" are NOT proof that a store is empty: the
 * database recovery clears the synced markers while the data stays, and a store written by a
 * version older than #6788 has no written marker at all. So the automatic switch also needs
 * POSITIVE evidence that this version created the store on this device. The app gets it by
 * asking the browser which IndexedDB databases exist just before the store's first open.
 */
export enum LocalStoreOrigin {
  /** The browser listed no database of this name just before the app first opened it. */
  CreatedHere = 'created-here',
  /** It was listed, or the browser could not say. Either way the store may hold data. */
  NotProvenNew = 'not-proven-new',
}

/** The localStorage key holding a store's `LocalStoreOrigin` (#6788). */
export function localStoreOriginKey(localStoreName: string): string {
  return `cs_local_store_origin_${localStoreName}`;
}

/** The recorded origin, or undefined when none is recorded yet. Unreadable reads as NotProvenNew. */
export function readLocalStoreOrigin(storage: GenerationStorage | undefined | null, localStoreName: string): LocalStoreOrigin | undefined {
  if (!storage) return LocalStoreOrigin.NotProvenNew;
  try {
    const raw = storage.getItem(localStoreOriginKey(localStoreName));
    if (raw == null) return undefined;
    return raw === LocalStoreOrigin.CreatedHere ? LocalStoreOrigin.CreatedHere : LocalStoreOrigin.NotProvenNew;
  } catch {
    // an unreadable origin proves nothing, the direction that keeps the store
    return LocalStoreOrigin.NotProvenNew;
  }
}

/**
 * Record a store's origin, once (#6788). Returns false when storage refused it; the caller
 * reports that, and the store then has no recorded origin, which never permits a switch.
 */
export function recordLocalStoreOrigin(storage: GenerationStorage | undefined | null, localStoreName: string, origin: LocalStoreOrigin): boolean {
  if (!storage) return false;
  try {
    storage.setItem(localStoreOriginKey(localStoreName), origin);
    return true;
  } catch {
    // the caller reports it; a missing origin already forbids the switch
    return false;
  }
}

/**
 * Decide a store's origin from the browser's list of IndexedDB databases (#6788).
 *
 * `listedNames` is undefined when the browser could not answer (no `databases()`, a throw, or
 * no answer in time): nothing is proven, so the store counts as NotProvenNew. PouchDB stores
 * a database under `_pouch_<name>`, and the match is exact, because a view index is a
 * separate database named `_pouch_<name>-mrview-<hash>`.
 */
export function decideLocalStoreOrigin(listedNames: readonly string[] | undefined, localStoreName: string): LocalStoreOrigin {
  if (listedNames === undefined) return LocalStoreOrigin.NotProvenNew;
  return listedNames.includes(`_pouch_${localStoreName}`) ? LocalStoreOrigin.NotProvenNew : LocalStoreOrigin.CreatedHere;
}

/**
 * Does a write of this document put data the user cares about into the store (#6788)?
 *
 * Telemetry events (`mix/…` queue items, `mix-server/…` copies for server-side sending) are
 * saved into the user's own store too, from the very first page — the subscribe page logs
 * the purchase before its reload. Counting them would mark every buyer's store "written"
 * and the switch would never fire for the people it exists for. Losing a few queued events
 * with an abandoned store is accepted. Every other document counts, a missing id included.
 */
export function isUserDataWrite(docId: string | undefined | null): boolean {
  if (typeof docId !== 'string') return true;
  return !(docId.startsWith('mix/') || docId.startsWith('mix-server/'));
}

/** The localStorage key saying this store has confirmed a write (#6788). */
export function localStoreWrittenKey(localStoreName: string): string {
  return `cs_local_store_written_${localStoreName}`;
}

/** The localStorage key saying the app already switched this account's store on its own (#6788). */
export function localStoreAutoSwitchedKey(baseName: string): string {
  return `cs_local_store_auto_switched_${baseName}`;
}

/**
 * Record that this store confirmed a write (#6788). Returns false when storage refused it,
 * so the caller can report that: a missing marker is what lets the app abandon a store.
 */
export function markLocalStoreWritten(storage: GenerationStorage | undefined | null, localStoreName: string): boolean {
  if (!storage) return false;
  try {
    if (storage.getItem(localStoreWrittenKey(localStoreName)) === '1') return true;
    storage.setItem(localStoreWrittenKey(localStoreName), '1');
    return true;
  } catch {
    // the caller reports it; there is nothing else to do with a storage that refuses
    return false;
  }
}

/**
 * Has this store ever confirmed a write (#6788)?
 *
 * An unreadable storage answers TRUE: that is the cautious direction, because "never
 * written" is the answer that lets the app abandon the store.
 */
export function hasLocalStoreBeenWritten(storage: GenerationStorage | undefined | null, localStoreName: string): boolean {
  if (!storage) return true;
  try {
    return storage.getItem(localStoreWrittenKey(localStoreName)) != null;
  } catch {
    // unreadable counts as written, the direction that keeps the store
    return true;
  }
}

/**
 * Has the app already switched this account's store on its own (#6788)?
 * Unreadable answers true, the cautious direction: it stops a second automatic switch.
 */
export function hasAutoSwitchedLocalStore(storage: GenerationStorage | undefined | null, baseName: string): boolean {
  if (!storage) return true;
  try {
    return storage.getItem(localStoreAutoSwitchedKey(baseName)) != null;
  } catch {
    // unreadable counts as already switched, the direction that stops a second switch
    return true;
  }
}

/**
 * Record that the app switched this account's store on its own (#6788). Returns false when
 * storage refused it — the caller then must not switch, or nothing would stop the next
 * silent store from being switched again.
 */
export function markAutoSwitchedLocalStore(storage: GenerationStorage | undefined | null, baseName: string): boolean {
  if (!storage) return false;
  try {
    storage.setItem(localStoreAutoSwitchedKey(baseName), '1');
    return storage.getItem(localStoreAutoSwitchedKey(baseName)) != null;
  } catch {
    // a flag that could not be recorded means no automatic switch; the caller reconnects instead
    return false;
  }
}

/**
 * Switch to the next generation WITHOUT asking (#6788), and return the name the app will
 * open from now on — or undefined when no switch was made.
 *
 * The once-only flag is recorded FIRST, and the switch happens only if it stuck: a storage
 * that refuses the flag would otherwise let every later silent store be switched again,
 * reload after reload. A storage that refuses the move itself leaves the app on the name
 * it has open, which is no switch either.
 */
export function switchLocalStoreWithoutAsking(
  storage: GenerationStorage | undefined | null,
  baseName: string,
  openName: string,
): string | undefined {
  if (!markAutoSwitchedLocalStore(storage, baseName)) return undefined;
  const newName = bumpLocalStoreGeneration(storage, baseName);
  return newName === openName ? undefined : newName;
}

/** What `mayAbandonSilentLocalStore` needs to know about one database (#6788). */
export interface SilentLocalStoreFacts {
  /** `DatabaseWrapper.internalDatabaseIdentifier`; only the user's own store ('user') qualifies. */
  databaseId: string;
  /** A wrapper that reads and writes the server directly has no local store to switch. */
  remoteOnly: boolean;
  /** `DatabaseWrapper.hasSyncedOnThisDeviceMarker`. */
  hasSyncedOnThisDevice: boolean;
  /** `readLocalStoreOrigin` for the name actually opened: only CreatedHere proves the store new. */
  origin: LocalStoreOrigin | undefined;
  /** `hasLocalStoreBeenWritten` for the name actually opened. */
  everWritten: boolean;
  /** A save of user data has passed the operation gate and not finished yet. It may still land. */
  userWritesInFlight: boolean;
  /** `hasAutoSwitchedLocalStore` for the base name. */
  alreadyAutoSwitched: boolean;
}

/**
 * May the app switch this silent store to a fresh name without asking (#6788)?
 *
 * Every condition must hold. Each one that fails keeps the old behaviour, which is the
 * same-name reconnect and, if that fails, the error page whose button asks first.
 */
export function mayAbandonSilentLocalStore(facts: SilentLocalStoreFacts): boolean {
  // the user's own database only: a coach's copy of a client and the shared databases are
  // not what the reset button covers, and the button's steps are what the switch reuses
  if (facts.databaseId !== 'user') return false;
  if (facts.remoteOnly) return false;
  // without proof this version created the store here, it may hold an older version's
  // writes — the written marker below only counts writes made since #6788
  if (facts.origin !== LocalStoreOrigin.CreatedHere) return false;
  // a device that finished a download may since have written things the server lacks
  if (facts.hasSyncedOnThisDevice) return false;
  if (facts.everWritten) return false;
  // a save already under way may still land in the store being given up
  if (facts.userWritesInFlight) return false;
  // at most once, so a fresh store that goes silent too cannot start a reload loop
  if (facts.alreadyAutoSwitched) return false;
  return true;
}

/** The localStorage key holding the generation for one database. */
export function localStoreGenerationKey(baseName: string): string {
  return `cs_local_store_generation_${baseName}`;
}

/**
 * How many times this device has been told to start its local store over.
 *
 * Anything unreadable counts as generation 0 — the original name. That direction is the
 * safe one: guessing low points the app at the store that already holds the user's data,
 * while guessing high would silently abandon it and re-download everything.
 */
export function readLocalStoreGeneration(storage: GenerationStorage | undefined | null, baseName: string): number {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(localStoreGenerationKey(baseName));
    if (raw == null) return 0;
    const value = Number.parseInt(raw, 10);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return value;
  } catch {
    // unreadable counts as generation 0, the safe direction — see above
    return 0;
  }
}

/**
 * The name to actually open, for a given base name and generation.
 *
 * Generation 0 is the plain base name, so every device that has never needed a reset
 * keeps exactly the name it has always had — this must never rename a working store.
 *
 * The suffix uses only letters, digits and underscores because this same string becomes
 * an IndexedDB database name in the browser AND a file name for the SQLite adapter on
 * iOS, and the two do not accept the same characters.
 */
export function localStoreNameFor(baseName: string, generation: number): string {
  if (!Number.isFinite(generation) || generation <= 0) return baseName;
  return `${baseName}__g${Math.floor(generation)}`;
}

/**
 * Take a generation suffix back off a local store name, giving the base name.
 *
 * The inverse of {@link localStoreNameFor}, and it lives here for one reason: the
 * suffix format is written in exactly one place and must be READ in exactly one place
 * too. #4981 phase 3's migration hand-wrote the pattern as `__gen<n>` and shipped a
 * regex that matched nothing — which silently made a reset device's own live database
 * look like another account's, dropped it from the migration, and blocked that device
 * permanently. A device that has been through a storage reset is precisely the
 * population that ticket family exists for, so the one spelling has one owner.
 *
 * A name with no suffix comes back unchanged.
 */
export function stripLocalStoreGeneration(localStoreName: string): string {
  return localStoreName.replace(/__g\d+$/, '');
}

/** The name to open for this database right now, reading the generation from storage. */
export function currentLocalStoreName(storage: GenerationStorage | undefined | null, baseName: string): string {
  return localStoreNameFor(baseName, readLocalStoreGeneration(storage, baseName));
}

/**
 * Start this device's local store over: move to the next generation and return the name
 * the app will ACTUALLY use from now on.
 *
 * "Actually" is the whole contract, and it is why this reads the value back instead of
 * returning what it just tried to write. A storage that refuses the write leaves the app
 * on the old name — the reset simply did not happen — and the caller reports this name to
 * telemetry, so returning the name it wanted would put a store nobody ever opened into
 * the report. The user finds out the same way either way: the boot fails again.
 *
 * The caller reloads the app afterwards. Nothing is deleted here — see the note at the
 * top of this file for why the old store is left where it is.
 */
export function bumpLocalStoreGeneration(storage: GenerationStorage | undefined | null, baseName: string): string {
  const next = readLocalStoreGeneration(storage, baseName) + 1;
  if (storage) {
    try {
      storage.setItem(localStoreGenerationKey(baseName), String(next));
    } catch {
      // nothing to do here beyond not claiming it worked — the read-back below is what
      // makes the returned name honest
    }
  }
  return currentLocalStoreName(storage, baseName);
}
